import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import {
  customerListWithBillToOption,
  resolveCustomerDisplayName,
  shouldMirrorBillToFromCustomerChange,
} from './utils';

type BillToSectionFormProps = {
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  tmsID?: number;
  prevCustomerExternalTMSIDRef: React.MutableRefObject<string | undefined>;
};

export default function BillToSectionForm({
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  tmsID,
  prevCustomerExternalTMSIDRef,
}: BillToSectionFormProps): React.JSX.Element {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<NormalizedLoad>();

  const watchedCustomer = watch('customer');
  const watchedBillTo = watch('billTo');

  const customersForBillTo = useMemo(
    () =>
      customerListWithBillToOption(customers, watchedBillTo, watchedCustomer),
    [customers, watchedBillTo, watchedCustomer]
  );

  const matchedCustomer = useMemo(
    () =>
      customers?.find(
        (c) => c.externalTMSID === watchedCustomer?.externalTMSID?.trim()
      ),
    [customers, watchedCustomer?.externalTMSID]
  );

  // Covers get-load / hydration when customer is set before the customers list catches up.
  useEffect(() => {
    const customerId = watchedCustomer?.externalTMSID?.trim();
    if (!customerId) {
      return;
    }

    const billToId = watchedBillTo?.externalTMSID?.trim();
    const previousCustomerId = prevCustomerExternalTMSIDRef.current;

    const shouldMirror = shouldMirrorBillToFromCustomerChange(
      billToId,
      customerId,
      previousCustomerId
    );

    if (!shouldMirror) {
      if (customerId !== previousCustomerId) {
        prevCustomerExternalTMSIDRef.current = customerId;
      }
      return;
    }

    const source = matchedCustomer ?? watchedCustomer;
    const displayName = resolveCustomerDisplayName(source);

    if (getValues('billTo.name') !== displayName) {
      setValue('billTo.name', displayName, {
        shouldDirty: false,
      });
    }
    if (getValues('billTo.externalTMSID') !== customerId) {
      setValue('billTo.externalTMSID', customerId, {
        shouldDirty: false,
      });
    }

    if (customerId !== previousCustomerId) {
      prevCustomerExternalTMSIDRef.current = customerId;
    }
  }, [
    getValues,
    matchedCustomer,
    prevCustomerExternalTMSIDRef,
    setValue,
    watchedBillTo?.externalTMSID,
    watchedBillTo?.name,
    watchedCustomer,
  ]);

  const billToSelectKey =
    watchedBillTo?.externalTMSID?.trim() ?? 'mgb-billto-empty';

  return (
    <RHFCustomerSelect<NormalizedLoad>
      key={billToSelectKey}
      disabled
      required
      name='billTo.externalTMSID'
      label='Name'
      control={control}
      errors={errors}
      customers={customersForBillTo}
      setCustomers={setCustomers}
      isLoadingCustomers={isLoadingCustomers}
      setIsLoadingCustomers={setIsLoadingCustomers}
      tmsID={tmsID}
    />
  );
}
